import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Package, Plus, RefreshCw, Loader2, Download, Upload } from 'lucide-react';
import { Modal } from './ui';
import {
  getAdminProducts,
  createAdminProduct,
  updateAdminProduct,
  setAdminProductActive,
  deleteAdminProduct,
  bulkAdminProducts,
  exportAdminProducts,
  importAdminProducts,
  type Product,
} from '../api';
import { extractApiErrorMessage } from '../utils/apiError';
import {
  useCatalogSelection,
  useCatalogBulk,
  CatalogToolbar,
  BulkActionBar,
  BulkResultNotice,
  CatalogRowActions,
  filterCatalog,
  type CatalogStatusFilter,
} from './catalog';

export function AdminProductCatalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    updated?: number;
    skipped?: number;
    total: number;
    errors?: Array<{ row: number; message: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAdminProducts(false);
      if (response.status && response.data) {
        setProducts(response.data);
      }
    } catch {
      setError('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Errors are thrown so the modal can render the server message (e.g. duplicate code → 409) inline.
  const handleCreate = async (name: string, category?: string, productCode?: string) => {
    const response = await createAdminProduct({ name, category, productCode });
    if (response.status && response.data) {
      setProducts(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setShowCreateModal(false);
    }
  };

  const handleUpdate = async (
    productId: string,
    name: string,
    category?: string,
    productCode?: string
  ) => {
    const response = await updateAdminProduct(productId, {
      name,
      category,
      // empty string asks the server to generate a fresh unique code
      productCode: productCode ?? '',
    });
    if (response.status && response.data) {
      setProducts(prev =>
        prev
          .map(p => (p.productId === productId ? response.data : p))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingProduct(null);
    }
  };

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      setError(null);
      const blob = await exportAdminProducts(); // active items only
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export products');
    } finally {
      setExporting(false);
    }
  }, []);

  const handleImport = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        setImporting(true);
        setError(null);
        setImportResult(null);
        const res = await importAdminProducts(file);
        if (res.status && res.data) {
          setImportResult(res.data);
          fetchProducts();
        } else {
          setError('Import failed');
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(msg || (err instanceof Error ? err.message : 'Failed to import products'));
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    },
    [fetchProducts]
  );

  // ---- Selection, search / filter, bulk actions ----
  const selection = useCatalogSelection();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CatalogStatusFilter>('active'); // delisted items hidden by default
  const filtered = useMemo(
    () =>
      filterCatalog(
        products,
        search,
        statusFilter,
        product => [product.name, product.productCode, product.category],
        product => product.isActive
      ),
    [products, search, statusFilter]
  );
  const visibleIds = useMemo(() => filtered.map(product => product.productId), [filtered]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every(id => selection.isSelected(id));

  // Drop selections for rows that disappeared after a refetch / delete
  const pruneSelection = selection.prune;
  useEffect(() => {
    pruneSelection(products.map(product => product.productId));
  }, [products, pruneSelection]);

  const bulk = useCatalogBulk<Product>({
    entityLabel: 'product',
    run: bulkAdminProducts,
    getId: product => product.productId,
    withActive: (product, isActive) => ({ ...product, isActive }),
    setItems: setProducts,
    selection,
    onError: setError,
  });

  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  /** Reversible: hides from the complaint form, keeps historical complaint links intact. */
  const handleToggleActive = async (product: Product) => {
    const next = !product.isActive;
    if (
      !next &&
      !window.confirm(
        'Deactivate this product? It will be hidden from the complaint form. Existing complaints are unaffected and you can reactivate it later.'
      )
    ) {
      return;
    }
    setRowBusyId(product.productId);
    setError(null);
    try {
      const response = await setAdminProductActive(product.productId, next);
      if (response.status && response.data) {
        setProducts(prev =>
          prev.map(item => (item.productId === product.productId ? response.data : item))
        );
      }
    } catch (err) {
      setError(
        extractApiErrorMessage(err, `Failed to ${next ? 'reactivate' : 'deactivate'} product`)
      );
    } finally {
      setRowBusyId(null);
    }
  };

  /** Permanent. The server refuses (409) when any complaint references the product. */
  const handleDelete = async (product: Product) => {
    if (
      !window.confirm(
        `Permanently delete "${product.name}"?\n\nThis cannot be undone. If any complaint references this product the delete is refused — deactivate it instead.`
      )
    ) {
      return;
    }
    setRowBusyId(product.productId);
    setError(null);
    try {
      const response = await deleteAdminProduct(product.productId);
      if (response.status) {
        setProducts(prev => prev.filter(item => item.productId !== product.productId));
        if (selection.isSelected(product.productId)) selection.toggle(product.productId);
      }
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to delete product'));
    } finally {
      setRowBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Product Catalog</h2>
            <p className="text-sm text-gray-500">
              Manage brand-level products for the complaint form
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={fetchProducts}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Export active items to Excel"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Import from Excel"
            >
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleImport}
              className="hidden"
            />
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {importResult && (
        <div className="mx-4 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <p>
            Imported {importResult.created} of {importResult.total} products.
            {(importResult.updated ?? 0) > 0 && (
              <span className="text-gray-700">
                {' '}
                {importResult.updated} existing product{importResult.updated === 1 ? '' : 's'}{' '}
                received a product code.
              </span>
            )}
            {(importResult.skipped ?? 0) > 0 && (
              <span className="text-gray-600">
                {' '}
                ({importResult.skipped ?? 0} already existed by code or name)
              </span>
            )}
          </p>
          {importResult.errors && importResult.errors.length > 0 && (
            <p className="mt-1 text-amber-700">
              {importResult.errors.length} row(s) failed:{' '}
              {importResult.errors.map(e => 'Row ' + e.row + ': ' + e.message).join('; ')}
            </p>
          )}
        </div>
      )}

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No products yet</p>
            <p className="text-sm mt-1">Add products to show in the complaint form dropdown</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <CatalogToolbar
              search={search}
              onSearchChange={setSearch}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              visibleCount={filtered.length}
              totalCount={products.length}
              searchPlaceholder="Search products by name, code or category…"
            />
            <BulkActionBar
              selectedCount={selection.count}
              allVisibleSelected={allVisibleSelected}
              visibleCount={filtered.length}
              onToggleAllVisible={() => selection.toggleAll(visibleIds)}
              onAction={bulk.runAction}
              onClear={selection.clear}
              busyAction={bulk.busyAction}
              entityLabel="product"
            />
            {bulk.result && (
              <BulkResultNotice
                result={bulk.result}
                entityLabel="product"
                onDismiss={bulk.dismissResult}
              />
            )}
            {filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                No products match your search or filter.
              </p>
            ) : (
              <div className="space-y-2">
                {filtered.map(product => {
                  const isActive = product.isActive;
                  const checked = selection.isSelected(product.productId);
                  const busy = rowBusyId === product.productId || bulk.busyAction !== null;
                  return (
                    <div
                      key={product.productId}
                      data-testid="catalog-row"
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg ${
                        checked
                          ? 'border-primary-300 bg-primary-50/40'
                          : isActive
                            ? 'border-gray-200 bg-white'
                            : 'border-gray-100 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => selection.toggle(product.productId)}
                          disabled={bulk.busyAction !== null}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                          aria-label={`Select ${product.name}`}
                        />
                        <div className="min-w-0">
                          <p
                            className={`font-medium break-words ${isActive ? 'text-gray-900' : 'text-gray-500'}`}
                          >
                            {product.name}
                            {!isActive && (
                              <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded bg-gray-200 text-gray-600 align-middle">
                                Inactive
                              </span>
                            )}
                          </p>
                          {(product.productCode || product.category) && (
                            <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                              {product.productCode && (
                                <span
                                  className="font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
                                  title="Product code"
                                >
                                  {product.productCode}
                                </span>
                              )}
                              {product.category && <span>{product.category}</span>}
                            </p>
                          )}
                        </div>
                      </div>
                      <CatalogRowActions
                        isActive={isActive}
                        onEdit={() => setEditingProduct(product)}
                        onToggleActive={() => handleToggleActive(product)}
                        onDelete={() => handleDelete(product)}
                        disabled={busy}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateProductModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
          existingCodes={codeSetOf(products)}
        />
      )}

      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={(name, category, productCode) =>
            handleUpdate(editingProduct.productId, name, category, productCode)
          }
          existingCodes={codeSetOf(products, editingProduct.productId)}
        />
      )}
    </div>
  );
}

const PRODUCT_CODE_HINT =
  "The distributor's code. Must be unique; import de-duplicates by it and it pre-fills on complaints.";
/** Lower-cased codes of every product in the catalog (optionally excluding one product). */
function codeSetOf(products: Product[], excludeProductId?: string): Set<string> {
  const set = new Set<string>();
  for (const p of products) {
    if (p.productId === excludeProductId || !p.productCode) continue;
    set.add(p.productCode.trim().toLowerCase());
  }
  return set;
}

function ProductCodeField({
  value,
  onChange,
  existingCodes,
  hint,
}: {
  value: string;
  onChange: (v: string) => void;
  existingCodes: Set<string>;
  hint: string;
}) {
  const duplicate = value.trim() !== '' && existingCodes.has(value.trim().toLowerCase());
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Product Code / SKU{' '}
        <span className="text-gray-400">(optional — auto-generated if left blank)</span>
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="e.g., LET-001"
        aria-invalid={duplicate || undefined}
        aria-describedby="product-code-help"
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 font-mono ${
          duplicate ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-gray-300'
        }`}
        data-testid="product-input-code"
      />
      {duplicate ? (
        <p id="product-code-help" className="mt-1 text-xs text-red-600" role="alert">
          This code is already used by another product. Codes must be unique.
        </p>
      ) : (
        <p id="product-code-help" className="mt-1 text-xs text-gray-500">
          {hint}
        </p>
      )}
    </div>
  );
}

function CreateProductModal({
  onClose,
  onSave,
  existingCodes,
}: {
  onClose: () => void;
  onSave: (name: string, category?: string, productCode?: string) => Promise<void>;
  existingCodes: Set<string>;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [productCode, setProductCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const duplicateCode =
    productCode.trim() !== '' && existingCodes.has(productCode.trim().toLowerCase());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || duplicateCode) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await onSave(name.trim(), category.trim() || undefined, productCode.trim() || undefined);
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err, 'Failed to create product'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Product">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Fresh Lettuce"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>
        <ProductCodeField
          value={productCode}
          onChange={setProductCode}
          existingCodes={existingCodes}
          hint={PRODUCT_CODE_HINT}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={category}
            onChange={e => setCategory(e.target.value)}
            placeholder="e.g., Produce"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
          />
        </div>
        {submitError && (
          <p className="text-sm text-red-600" role="alert" data-testid="product-modal-error">
            {submitError}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim() || duplicateCode}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Product'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditProductModal({
  product,
  onClose,
  onSave,
  existingCodes,
}: {
  product: Product;
  onClose: () => void;
  onSave: (name: string, category?: string, productCode?: string) => Promise<void>;
  existingCodes: Set<string>;
}) {
  const [name, setName] = useState(product.name);
  const [category, setCategory] = useState(product.category || '');
  const [productCode, setProductCode] = useState(product.productCode || '');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const duplicateCode =
    productCode.trim() !== '' && existingCodes.has(productCode.trim().toLowerCase());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || duplicateCode) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await onSave(name.trim(), category.trim() || undefined, productCode.trim() || undefined);
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err, 'Failed to update product'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit Product">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>
        <ProductCodeField
          value={productCode}
          onChange={setProductCode}
          existingCodes={existingCodes}
          hint={`${PRODUCT_CODE_HINT} Clearing it generates a new code.`}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
          />
        </div>
        {submitError && (
          <p className="text-sm text-red-600" role="alert" data-testid="product-modal-error">
            {submitError}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim() || duplicateCode}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
